import { Menu } from "@headlessui/react";
import { ChartBarIcon, RefreshIcon } from "@heroicons/react/outline";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import Sidebar from "../../../components/Sidebar/Sidebar";
import { useAuthToken } from "../../../hooks/useAuthToken";
import { useAuth } from "../../../utils/auth/auth";
import { apiV3 } from "../../../utils/helpers/api";
import {
  DashboardDataset,
  DashboardForecast,
  DashboardForecastSummary,
  DashboardStats,
  ForecastTotal,
} from "../../../utils/types";
import StatsCard from "./StatsCard";
import {
  DASHBOARD_TIMEZONE,
  currentSlot,
  DashboardPeriod,
  fetchesForecast,
  forecastMonthOptions,
  forecastRange,
  hasForecast,
  hourLabel,
  isForecastOnly,
  isSingleDay,
  longDay,
  PERIOD_OPTIONS,
  periodLabel,
  periodRange,
  shortDay,
} from "./dashboardPeriods";

// Melbourne Airport brand (brand.md): Deep CBD Blue for the series, Electric
// Blue for the hover cursor, Evening Blue for grid lines, charcoal for text.
const SERIES = "#00345E";
const SERIES_SOFT = "#295DA8";
const SERIES_FAINT = "#9CB3C9";
const GRID = "#CDDCDF";
const INK = "#151818";
const MUTED = "#6B7280";

/** Portal wording for each dataset; matches the sidebar page names. */
const DATASET_OPTIONS: Array<{ value: DashboardDataset; label: string; noun: string }> = [
  { value: "person-security", label: "Person Audits", noun: "person audits" },
  { value: "self-serve", label: "Self Serve Audits", noun: "self serve check-ins" },
];

const ALL_LOCATIONS = "";

const datasetNoun = (dataset: DashboardDataset): string =>
  DATASET_OPTIONS.find((o) => o.value === dataset)?.noun ?? "submissions";

const segmentClass = (active: boolean): string =>
  "px-3 py-2 text-sm font-medium border-r border-gray-300 last:border-r-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#295DA8] disabled:cursor-not-allowed " +
  (active ? "bg-[#00345E] text-white" : "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100");

const tooltipStyle = {
  borderRadius: "8px",
  border: `1px solid ${GRID}`,
  boxShadow: "0 8px 24px -8px rgba(0, 52, 94, 0.25)",
  fontSize: "12px",
  padding: "8px 12px",
};

const num = (value: number): string => value.toLocaleString("en-AU");

/**
 * What a forecast was fetched for. A result is only shown while the page is
 * still asking for the same thing, so a slow response for an earlier period
 * can never be drawn over a newer one.
 */
const forecastKeyFor = (
  dataset: DashboardDataset | undefined,
  location: string | undefined,
  window: { fromDate: string; toDate: string }
): string => [dataset ?? "scoped", location ?? "", window.fromDate, window.toDate].join("|");

/** Chip text for a counter's forecast; nothing until it has loaded. */
const forecastBadge = (total: ForecastTotal | undefined): string | null =>
  total ? `Forecast ≈ ${num(total.total)}` : null;

const signedPercent = (value: number | null): string =>
  value === null ? "n/a" : `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;

type DailyPoint = {
  date: string;
  label: string;
  actual: number | null;
  forecast: number | null;
  lastYear: number | null;
};

/** One bar of the hour chart: an hour, or a half hour on single-day views. */
type HourPoint = {
  label: string;
  /** Actual (or average) submissions; null for the rest of today. */
  value: number | null;
  /** Forecast for a half hour of today still to come; null otherwise. */
  forecast: number | null;
  /** Today's forecast for the slot, past or future, for the tooltip. */
  expected: number | null;
};

const round1 = (n: number): number => Math.round(n * 10) / 10;

function HourTooltip({
  active,
  payload,
  label,
  valueName,
  showExpected,
}: TooltipProps<number, string> & { valueName: string; showExpected: boolean }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload as HourPoint;
  return (
    <div style={{ ...tooltipStyle, background: "#fff" }}>
      <p className="font-medium text-[#151818] mb-1">{label}</p>
      {point.value !== null && (
        <p className="text-gray-700">
          {valueName}: {num(point.value)}
        </p>
      )}
      {showExpected && point.expected !== null && (
        <p className="text-gray-500">Expected: {num(point.expected)}</p>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Sidebar />
      <div className="flex flex-col flex-1 md:pl-64">
        <main className="flex-1">{children}</main>
      </div>
    </>
  );
}

function ChartCard({
  title,
  subtitle,
  aside,
  loading,
  children,
}: {
  title: string;
  subtitle: string;
  aside?: React.ReactNode;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-base font-semibold text-[#151818]">{title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        {aside}
      </div>
      <div
        className={
          "relative transition-opacity duration-200 " + (loading ? "opacity-40" : "opacity-100")
        }
        aria-busy={loading}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * Overview of submissions: today/month counters, daily trend, distribution by
 * location, and hour-of-day profile. All counting happens server-side in one
 * request per change.
 *
 * "This Month" adds the backend's forecast for the month (made as at the 1st)
 * over the actuals so far, so the two can be compared. "Next Month" is
 * forecast only; for person audits its drop-down moves it on to either of the
 * two months after. "Today" and "Yesterday" show the day in half hours, and
 * Today fills the half hours still to come with the day's forecast.
 *
 * The forecast reads a year and a half of history and is the slow request,
 * so the actuals are shown the moment they arrive and the forecast is layered
 * on when it is done; only a forecast-only view waits for it.
 *
 * The Today and This Month counters carry the matching forecast as a chip,
 * loaded once per dataset since the counters never depend on the period.
 *
 * Admin and Airport Admin can switch between person audits and self serve
 * check-ins across every location, and narrow the breakdown to one location.
 * A Self Serve Admin gets self serve check-ins only, limited by the backend
 * to their own location.
 */
export default function DashboardV3() {
  const auth = useAuth();
  useAuthToken();

  const role: number | undefined = auth.user?.role;
  const canView = role === 1 || role === 2 || role === 4;
  const canChooseDataset = role === 1 || role === 2;

  const [period, setPeriod] = useState<DashboardPeriod>("today");
  const [dataset, setDataset] = useState<DashboardDataset>(
    canChooseDataset ? "person-security" : "self-serve"
  );
  const [location, setLocation] = useState<string>(ALL_LOCATIONS);
  // Which coming month "Next Month" forecasts: 1 is the month actually next.
  const [monthsAhead, setMonthsAhead] = useState<number>(1);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [forecastResult, setForecastResult] = useState<{
    key: string;
    data: DashboardForecast;
  } | null>(null);
  const [forecastSummary, setForecastSummary] = useState<DashboardForecastSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [forecastLoading, setForecastLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  // Only the latest load may touch the page: the user can change period
  // again while an earlier, slower response is still on its way.
  const loadId = useRef(0);
  const load = useCallback(
    async (
      selectedPeriod: DashboardPeriod,
      selectedDataset: DashboardDataset,
      selectedLocation: string,
      selectedMonthsAhead: number
    ) => {
      const id = ++loadId.current;
      const current = () => id === loadId.current;
      setLoading(true);
      setError("");

      // A Self Serve Admin's dataset and location are fixed by the backend.
      const requestedDataset = canChooseDataset ? selectedDataset : undefined;
      const requestedLocation =
        canChooseDataset && selectedLocation ? selectedLocation : undefined;

      // The forecast is not awaited with the actuals: it arrives when it
      // arrives and the charts pick it up then.
      if (fetchesForecast(selectedPeriod)) {
        const window = forecastRange(selectedPeriod, selectedMonthsAhead);
        const key = forecastKeyFor(requestedDataset, requestedLocation, window);
        setForecastLoading(true);
        apiV3
          .getPersonSecuritySubmissionsForecast(
            window.fromDate,
            window.toDate,
            DASHBOARD_TIMEZONE,
            requestedDataset,
            requestedLocation
          )
          .then((response) => {
            if (current()) setForecastResult({ key, data: response.data });
          })
          .catch((err) => {
            console.error("Error occurred while loading the dashboard forecast -", err);
            if (current()) setError("Could not load the forecast. Try refreshing in a moment.");
          })
          .finally(() => {
            if (current()) setForecastLoading(false);
          });
      } else {
        setForecastLoading(false);
      }

      try {
        // Actuals for the period. A coming month has none, but the counters at
        // the top still want fresh numbers, so a one-day call keeps them.
        const { fromDate, toDate } = periodRange(selectedPeriod);
        const response = await apiV3.getPersonSecuritySubmissionsDashboard(
          fromDate,
          toDate,
          DASHBOARD_TIMEZONE,
          requestedDataset,
          requestedLocation
        );
        if (current()) {
          setStats(response.data);
          setUpdatedAt(new Date());
        }
      } catch (err) {
        console.error("Error occurred while loading the dashboard -", err);
        if (current()) setError("Could not load dashboard data. Try refreshing in a moment.");
      } finally {
        if (current()) setLoading(false);
      }
    },
    [canChooseDataset]
  );

  useEffect(() => {
    if (canView) load(period, dataset, location, monthsAhead);
  }, [canView, period, dataset, location, monthsAhead, load]);

  // The counters' forecasts: whole scope, so only the dataset matters. A
  // slow or failed load just leaves the chips off; the counters still show.
  const summaryRequest = useRef(0);
  const loadForecastSummary = useCallback(
    async (selectedDataset: DashboardDataset) => {
      const requestId = ++summaryRequest.current;
      setForecastSummary(null);
      try {
        const response = await apiV3.getPersonSecuritySubmissionsForecastSummary(
          DASHBOARD_TIMEZONE,
          canChooseDataset ? selectedDataset : undefined
        );
        if (requestId === summaryRequest.current) setForecastSummary(response.data);
      } catch (err) {
        console.error("Error occurred while loading the dashboard forecasts -", err);
      }
    },
    [canChooseDataset]
  );

  useEffect(() => {
    if (canView) loadForecastSummary(dataset);
  }, [canView, dataset, loadForecastSummary]);

  // A location only makes sense within the dataset it came from, and only
  // person audits can look past next month.
  const changeDataset = (next: DashboardDataset) => {
    setLocation(ALL_LOCATIONS);
    setMonthsAhead(1);
    setDataset(next);
  };

  const forecastMonths = forecastMonthOptions();
  const canChooseForecastMonth = dataset === "person-security";
  const chooseForecastMonth = (next: number) => {
    setMonthsAhead(next);
    setPeriod("nextMonth");
  };

  // The forecast on screen must be the one this view asked for.
  const forecastKey = fetchesForecast(period)
    ? forecastKeyFor(
        canChooseDataset ? dataset : undefined,
        canChooseDataset && location ? location : undefined,
        forecastRange(period, monthsAhead)
      )
    : null;
  const forecast =
    forecastResult && forecastKey !== null && forecastResult.key === forecastKey
      ? forecastResult.data
      : null;
  const forecastPending = forecastKey !== null && forecast === null && forecastLoading;
  // A forecast-only view has nothing to draw until the forecast lands.
  const chartsLoading = loading || (isForecastOnly(period) && forecastPending);

  const showForecast = hasForecast(period) && forecast !== null;
  const forecastOnly = showForecast && isForecastOnly(period);
  const singleDay = isSingleDay(period);
  // Today's own forecast (a one-day window) fills in the rest of the day.
  const todayForecast = period === "today" && forecast !== null && forecast.forecast.days === 1;

  // What the numbers describe, in the portal's own words.
  const shownDataset = stats?.dataset ?? dataset;
  const scopedLocations = stats?.locations ?? null;
  const scopeText = scopedLocations
    ? `at ${scopedLocations.join(", ")}`
    : "across all checkpoints";
  // A single scoped location makes the location chart a one-bar chart.
  const showLocationChart = !scopedLocations || scopedLocations.length > 1;
  const canFilterLocation = canChooseDataset && showLocationChart;

  // Days behind the hour-of-day average: the actual range, or the forecast
  // window when there are no actuals.
  const days = forecastOnly ? forecast.forecast.days : stats?.range.days ?? 1;
  const multiDay = days > 1;

  // byLocation always covers the whole scope (the filter narrows only the
  // day and hour breakdowns), so it doubles as the location picker's list.
  const locationRows = useMemo(
    () => (forecastOnly ? forecast.byLocation : stats?.byLocation ?? []),
    [forecastOnly, forecast, stats]
  );
  const locationOptions = useMemo(() => {
    const names = locationRows.map((row) => row.location);
    if (location && !names.includes(location)) names.push(location);
    return names;
  }, [locationRows, location]);

  const dailyData = useMemo((): DailyPoint[] => {
    if (!showForecast) {
      return (stats?.byDay ?? []).map((d) => ({
        date: d.date,
        label: shortDay(d.date),
        actual: d.count,
        forecast: null,
        lastYear: null,
      }));
    }
    // The forecast spans the whole month; actuals stop at today.
    const actuals = new Map((stats?.byDay ?? []).map((d) => [d.date, d.count]));
    return forecast.byDay.map((d) => ({
      date: d.date,
      label: shortDay(d.date),
      actual: forecastOnly ? null : actuals.get(d.date) ?? null,
      forecast: d.count,
      lastYear: d.lastYear,
    }));
  }, [showForecast, forecastOnly, forecast, stats]);
  const showDailyChart = showForecast || multiDay;
  const showLastYear = forecastOnly && forecast.method.basis === "last-year";

  // How the forecast is tracking against the actuals so far this month.
  const toDate = useMemo(() => {
    if (!showForecast || forecastOnly) return null;
    let actual = 0;
    let expected = 0;
    for (const point of dailyData) {
      if (point.actual === null) continue;
      actual += point.actual;
      expected += point.forecast ?? 0;
    }
    return { actual, expected, percent: expected > 0 ? ((actual - expected) / expected) * 100 : null };
  }, [showForecast, forecastOnly, dailyData]);

  // The half hour the airport clock was in when the data was loaded: later
  // slots of today are forecast, not actual.
  const nowSlot = currentSlot(updatedAt ?? undefined);

  const hourlyData = useMemo((): HourPoint[] => {
    const point = (label: string, value: number): HourPoint => ({
      label,
      value,
      forecast: null,
      expected: null,
    });
    if (forecastOnly) {
      return forecast.byHour.map((h) => point(hourLabel(h.hour), h.count));
    }
    // Half hours need a backend that sends them; until then, hours.
    if (singleDay && stats?.byHalfHour) {
      const expectedSlots = todayForecast ? forecast.byHalfHour ?? null : null;
      return stats.byHalfHour.map((h) => {
        const label = hourLabel(h.hour, h.minute);
        if (!expectedSlots) return point(label, h.count);
        const expected = expectedSlots[h.slot]?.count ?? 0;
        const future = h.slot > nowSlot;
        return {
          label,
          value: future ? null : h.count,
          forecast: future ? expected : null,
          expected,
        };
      });
    }
    return (stats?.byHour ?? []).map((h) =>
      // Multi-day periods show a typical day, not the sum of all days.
      point(hourLabel(h.hour), multiDay ? round1(h.count / days) : h.count)
    );
  }, [forecastOnly, singleDay, todayForecast, forecast, stats, multiDay, days, nowSlot]);

  // How today is tracking against its forecast, over the half hours so far.
  const todaySoFar = useMemo(() => {
    if (!todayForecast) return null;
    let actual = 0;
    let expected = 0;
    for (const p of hourlyData) {
      if (p.value === null) continue;
      actual += p.value;
      expected += p.expected ?? 0;
    }
    return { actual, expected };
  }, [todayForecast, hourlyData]);

  if (!canView) {
    return (
      <Shell>
        <div className="p-8">
          <p className="text-sm text-gray-600">Only administrators can view the dashboard.</p>
        </div>
      </Shell>
    );
  }

  const label = periodLabel(period, monthsAhead);
  const locationText = location ? ` · ${location}` : "";
  let breakdownTotal = label;
  if (forecastOnly) {
    breakdownTotal = `≈ ${num(forecast.forecast.total)} submissions expected · ${shortDay(
      forecast.forecast.fromDate
    )} – ${shortDay(forecast.forecast.toDate)}${locationText}`;
  } else if (showForecast && stats) {
    breakdownTotal = `${num(stats.range.total)} submissions so far · ${label} · forecast ≈ ${num(
      forecast.forecast.total
    )} for the full month${locationText}`;
  } else if (todayForecast && stats) {
    breakdownTotal = `${num(stats.range.total)} submissions so far · ${label} · forecast ≈ ${num(
      forecast.forecast.total
    )} for the day${locationText}`;
  } else if (stats) {
    breakdownTotal = `${num(stats.range.total)} submissions · ${label}${locationText}`;
  }
  if (forecastPending) {
    breakdownTotal += " · forecast loading…";
  }

  return (
    <Shell>
      <div className="py-4">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="flex flex-row items-center text-2xl font-bold text-gray-900">
                <ChartBarIcon className="h-6 w-6 mr-1.5" />
                Dashboard
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {datasetNoun(shownDataset).replace(/^./, (c) => c.toUpperCase())} {scopeText}, in
                Melbourne local time.
              </p>
            </div>
            <div className="flex flex-row items-center gap-3 mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
              {updatedAt && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  Updated{" "}
                  {updatedAt.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  load(period, dataset, location, monthsAhead);
                  loadForecastSummary(dataset);
                }}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#295DA8] focus-visible:ring-offset-2 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshIcon className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-md border border-[#F15F4B] bg-[#FDECE9] px-4 py-3 text-sm text-[#B8422F]">
              {error}
            </div>
          )}

          {/* Dataset: admins choose; a Self Serve Admin sees their scope instead */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {canChooseDataset ? (
              <div
                role="radiogroup"
                aria-label="Dataset"
                className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm overflow-hidden"
              >
                {DATASET_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={option.value === dataset}
                    onClick={() => changeDataset(option.value)}
                    disabled={loading}
                    className={segmentClass(option.value === dataset)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center rounded-md bg-[#CDDCDF] px-3 py-1.5 text-sm font-medium text-[#00345E]">
                Self Serve Audits
                {scopedLocations && scopedLocations.length > 0 && (
                  <span className="ml-2 text-[#151818]">· {scopedLocations.join(", ")}</span>
                )}
              </span>
            )}
          </div>

          {/* Counters */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatsCard
              label="Today"
              value={stats?.summary.today ?? 0}
              description="Submissions so far today"
              badge={forecastBadge(forecastSummary?.today)}
              badgeTitle="Expected total for today, forecast this morning"
              loading={loading && !stats}
            />
            <StatsCard
              label="Yesterday"
              value={stats?.summary.yesterday ?? 0}
              description="Submissions yesterday"
              loading={loading && !stats}
            />
            <StatsCard
              label="This Month"
              value={stats?.summary.thisMonth ?? 0}
              description="Month to date"
              badge={forecastBadge(forecastSummary?.thisMonth)}
              badgeTitle="Expected total for the month, forecast on the 1st"
              loading={loading && !stats}
            />
            <StatsCard
              label="Last Month"
              value={stats?.summary.lastMonth ?? 0}
              description="Full previous month"
              loading={loading && !stats}
            />
          </div>

          {/* Period + location selectors */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                Breakdown
                {forecastOnly && (
                  <span className="inline-flex items-center rounded-full bg-[#CDDCDF] px-2 py-0.5 text-xs font-medium text-[#00345E]">
                    Forecast
                  </span>
                )}
              </h2>
              <p className="text-xs text-gray-400">{breakdownTotal}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {canFilterLocation && (
                <select
                  aria-label="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={loading && locationOptions.length === 0}
                  className="rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#295DA8] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value={ALL_LOCATIONS}>All locations</option>
                  {locationOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              )}
              <div
                role="radiogroup"
                aria-label="Period"
                className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm"
              >
                {/* No overflow clipping here (the month menu hangs below), so
                    the end segments round their own corners. */}
                {PERIOD_OPTIONS.map((option) => {
                  const active = option.value === period;
                  // Named after its month once it is not the month actually next.
                  const chosenMonth =
                    option.value === "nextMonth" && monthsAhead > 1
                      ? forecastMonths.find((month) => month.monthsAhead === monthsAhead)
                      : undefined;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setPeriod(option.value)}
                      disabled={loading}
                      className={segmentClass(active) + " first:rounded-l-md last:rounded-r-md"}
                    >
                      {chosenMonth ? chosenMonth.shortLabel : option.label}
                    </button>
                  );
                })}
                {canChooseForecastMonth && (
                  <Menu as="div" className="relative flex">
                    <Menu.Button
                      aria-label="Choose forecast month"
                      disabled={loading}
                      className={
                        segmentClass(period === "nextMonth") + " rounded-r-md px-2 !border-r-0"
                      }
                    >
                      <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                    </Menu.Button>
                    <Menu.Items className="absolute right-0 top-full z-10 mt-1 w-48 origin-top-right rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
                      {forecastMonths.map((month) => {
                        const chosen = period === "nextMonth" && month.monthsAhead === monthsAhead;
                        return (
                          <Menu.Item key={month.monthsAhead}>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => chooseForecastMonth(month.monthsAhead)}
                                className={
                                  "flex w-full items-center justify-between px-3 py-2 text-left text-sm " +
                                  (active ? "bg-[#CDDCDF] " : "") +
                                  (chosen ? "font-semibold text-[#00345E]" : "text-gray-700")
                                }
                              >
                                {month.label}
                                {chosen && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
                              </button>
                            )}
                          </Menu.Item>
                        );
                      })}
                    </Menu.Items>
                  </Menu>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-6 pb-10">
            {/* Daily trend: only meaningful across more than one day */}
            {showDailyChart && (
              <ChartCard
                title={
                  forecastOnly
                    ? "Expected submissions per day"
                    : showForecast
                    ? "Submissions per day vs forecast"
                    : "Submissions per day"
                }
                subtitle={
                  forecastOnly
                    ? `Forecast daily volume · ${label}${locationText}`
                    : toDate
                    ? `Actual so far ${num(toDate.actual)} vs forecast ${num(toDate.expected)} (${signedPercent(
                        toDate.percent
                      )}) · ${label}${locationText}`
                    : `Daily volume · ${label}${locationText}`
                }
                loading={chartsLoading}
              >
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={dailyData}
                      margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="dashboardDailyFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={SERIES} stopOpacity={0.18} />
                          <stop offset="95%" stopColor={SERIES} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: MUTED, fontSize: 11 }}
                        dy={8}
                        minTickGap={16}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        tick={{ fill: MUTED, fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        cursor={{ stroke: SERIES_SOFT, strokeWidth: 1 }}
                        labelFormatter={(_, payload) =>
                          payload && payload[0] ? longDay(payload[0].payload.date) : ""
                        }
                      />
                      {showForecast && (
                        <Legend
                          verticalAlign="top"
                          align="right"
                          iconType="plainline"
                          wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
                        />
                      )}
                      {forecastOnly ? (
                        <Area
                          type="monotone"
                          dataKey="forecast"
                          name="Forecast"
                          stroke={SERIES}
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          fill="url(#dashboardDailyFill)"
                          dot={{ r: 2.5, fill: SERIES, strokeWidth: 0 }}
                          activeDot={{ r: 4, fill: SERIES_SOFT, strokeWidth: 0 }}
                          isAnimationActive={false}
                        />
                      ) : (
                        <Area
                          type="monotone"
                          dataKey="actual"
                          name={showForecast ? "Actual" : "Submissions"}
                          stroke={SERIES}
                          strokeWidth={2}
                          fill="url(#dashboardDailyFill)"
                          connectNulls={false}
                          dot={
                            dailyData.length <= 31
                              ? { r: 2.5, fill: SERIES, strokeWidth: 0 }
                              : false
                          }
                          activeDot={{ r: 4, fill: SERIES_SOFT, strokeWidth: 0 }}
                          isAnimationActive={false}
                        />
                      )}
                      {showForecast && !forecastOnly && (
                        <Line
                          type="monotone"
                          dataKey="forecast"
                          name="Forecast"
                          stroke={SERIES_SOFT}
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          dot={false}
                          activeDot={{ r: 3, fill: SERIES_SOFT, strokeWidth: 0 }}
                          isAnimationActive={false}
                        />
                      )}
                      {showLastYear && (
                        <Line
                          type="monotone"
                          dataKey="lastYear"
                          name="Same weeks last year"
                          stroke={SERIES_FAINT}
                          strokeWidth={1.5}
                          dot={false}
                          activeDot={{ r: 3, fill: SERIES_FAINT, strokeWidth: 0 }}
                          isAnimationActive={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            )}

            {/* Hour-of-day profile */}
            <ChartCard
              title={
                forecastOnly
                  ? "Expected day by hour"
                  : multiDay
                  ? "Typical day by hour"
                  : todayForecast
                  ? "Submissions by half hour vs forecast"
                  : "Submissions by half hour"
              }
              subtitle={
                forecastOnly
                  ? `Expected submissions per hour on an average day, shaped by the previous 13 weeks${locationText}`
                  : multiDay
                  ? `Average submissions per hour of day across ${days} days · ${label}${locationText}`
                  : todaySoFar
                  ? `Actual so far ${num(todaySoFar.actual)} vs expected ${num(
                      todaySoFar.expected
                    )} · the rest of today is forecast · ${label}${locationText}`
                  : `Submissions per half hour · ${label}${locationText}`
              }
              loading={chartsLoading}
            >
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: MUTED, fontSize: 11 }}
                      dy={8}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={multiDay}
                      tick={{ fill: MUTED, fontSize: 11 }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(205, 220, 223, 0.35)" }}
                      content={
                        <HourTooltip
                          valueName={
                            forecastOnly
                              ? "Expected per day"
                              : multiDay
                              ? "Average per day"
                              : "Submissions"
                          }
                          showExpected={todayForecast}
                        />
                      }
                    />
                    {todayForecast && (
                      <Legend
                        verticalAlign="top"
                        align="right"
                        iconType="square"
                        wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
                      />
                    )}
                    {/* Actual and forecast share a column: each slot has one or the other. */}
                    <Bar
                      dataKey="value"
                      name={todayForecast ? "Actual" : "Submissions"}
                      stackId="slot"
                      fill={forecastOnly ? SERIES_SOFT : SERIES}
                      radius={[3, 3, 0, 0]}
                      isAnimationActive={false}
                    />
                    {todayForecast && (
                      <Bar
                        dataKey="forecast"
                        name="Forecast"
                        stackId="slot"
                        fill={SERIES_FAINT}
                        radius={[3, 3, 0, 0]}
                        isAnimationActive={false}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {/* Location distribution: every checkpoint, chosen one highlighted */}
            {showLocationChart && (
              <ChartCard
                title={forecastOnly ? "Expected submissions by location" : "Submissions by location"}
                subtitle={
                  forecastOnly
                    ? "Forecast total per checkpoint, split by each checkpoint's share of the previous 13 weeks"
                    : `Total entries per checkpoint · ${label}`
                }
                aside={
                  locationRows.length > 0 ? (
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {locationRows.length} location{locationRows.length === 1 ? "" : "s"}
                    </span>
                  ) : undefined
                }
                loading={chartsLoading}
              >
                {locationRows.length === 0 && !chartsLoading ? (
                  <p className="text-sm text-gray-500 py-10 text-center">
                    No submissions in this period.
                  </p>
                ) : (
                  <div style={{ height: Math.max(220, 36 * locationRows.length + 40) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={locationRows}
                        layout="vertical"
                        margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
                        barCategoryGap={8}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID} />
                        <XAxis
                          type="number"
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          tick={{ fill: MUTED, fontSize: 11 }}
                        />
                        <YAxis
                          type="category"
                          dataKey="location"
                          width={140}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: INK, fontSize: 12 }}
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          cursor={{ fill: "rgba(205, 220, 223, 0.35)" }}
                          formatter={(value) => [value, forecastOnly ? "Expected" : "Submissions"]}
                        />
                        <Bar
                          dataKey="count"
                          name="Submissions"
                          fill={SERIES}
                          radius={[0, 3, 3, 0]}
                          barSize={22}
                          isAnimationActive={false}
                        >
                          {locationRows.map((row) => (
                            <Cell
                              key={row.location}
                              fill={!location || row.location === location ? SERIES : SERIES_FAINT}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </ChartCard>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
